import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { ball, box, leaf, mesh, tube } from './art';

export const PICNIC = { x: 264, z: 72, name: 'The checkered picnic' };
export const BERRY_START = { x: 272, z: 70 };
export function picnicReserved(x: number, z: number): boolean { return x >= 242 && x <= 286 && z >= 50 && z <= 92; }

export class Picnic {
  readonly group = new THREE.Group();
  readonly berry = new THREE.Group();
  steps: number;
  private targetZ: number;
  private lastTime = 0;
  constructor(parent: THREE.Group, steps = 0) {
    this.steps = steps > 0 ? 1 : 0; this.targetZ = 70 + this.steps * 10.5;
    parent.add(this.group); this.group.add(this.berry);
    this.berry.position.set(272, 1.4, this.targetZ);
    const blanket = box(this.group, 264, .06, 72, 40, .12, 36, '#f1e6de');
    blanket.receiveShadow = true;
    // Shared geometry keeps the woven checks inexpensive to render.
    const tile = new THREE.PlaneGeometry(4, 4);
    const checks = ['#d8919a', '#e8c1c4'].map(color => new THREE.InstancedMesh(tile, new THREE.MeshStandardMaterial({ color, roughness: 1 }), 45));
    checks.forEach(m => { m.castShadow = false; m.receiveShadow = true; this.group.add(m); });
    const transform = new THREE.Object3D(); transform.rotation.x = -Math.PI / 2;
    const indices = [0, 0];
    for (let x = 0; x < 10; x++) for (let z = 0; z < 9; z++) {
      const color = x % 2 === z % 2 ? 0 : 1;
      transform.position.set(246 + x * 4, .125, 56 + z * 4); transform.updateMatrix(); checks[color].setMatrixAt(indices[color]++, transform.matrix);
    }
    for (let i = 0; i < 35; i++) {
      tube(this.group, [new THREE.Vector3(245 + i * 1.1, .13, 90), new THREE.Vector3(245 + i * 1.1, .08, 91.3)], .035, '#eadbd6');
    }
    const fold = new RoundedBoxGeometry(12, 1.6, 1, 3, .35);
    for (const z of [62, 78]) mesh(fold, '#bd7888', this.group, 277, .8, z);
    mesh(new RoundedBoxGeometry(1, 1.6, 16, 3, .35), '#bd7888', this.group, 283, .8, 70);
    for (const z of [64, 76]) mesh(new RoundedBoxGeometry(1, 1.6, 3, 3, .35), '#bd7888', this.group, 272, .8, z);
    const bread = new RoundedBoxGeometry(9, 1.2, 8, 4, .5);
    for (const [y, color] of [[.7, '#d9b87d'], [1.5, '#739955'], [1.9, '#e4ba56'], [2.7, '#d9b87d']] as const) {
      if (y === 1.5 || y === 1.9) box(this.group, 251, y, 66, 9, .35, 8, color);
      else mesh(bread, color, this.group, 251, y, 66);
    }
    for (let i = 0; i < 6; i++) ball(this.group, 247.5 + i * 1.3, 3.35, 64.5 + i % 2, .12, .04, .08, '#f3e5b7');
    ball(this.berry, 0, 0, 0, 1.65, 1.4, 1.65, '#b84561');
    for (let i = 0; i < 18; i++) { const a = i * 2.4; const y = -.8 + (i % 5) * .4; const r = 1.65 * Math.sqrt(1 - (y / 1.4) ** 2); ball(this.berry, Math.cos(a) * r, y, Math.sin(a) * r, .08, .13, .07, '#f5cd86'); }
    for (let i = 0; i < 5; i++) leaf(this.berry, 0, 1.25, 0, 1.4, .3, '#548257', i * Math.PI * 2 / 5);
    const ring = new THREE.Group(); ring.name = 'picnic-diamond-ring'; ring.position.set(278, .28, 70); this.group.add(ring);
    const silver = new THREE.MeshStandardMaterial({ color: '#dce5eb', metalness: .75, roughness: .2 });
    mesh(new THREE.TorusGeometry(.85, .14, 12, 40), silver, ring).rotation.x = -Math.PI / 2;
    const setting = new THREE.Group(); setting.name = 'diamond-setting'; setting.position.set(0, .13, -.85); ring.add(setting);
    mesh(new THREE.CylinderGeometry(.25, .18, .16, 12), silver, setting, 0, .08, 0);
    for (const x of [-.19, .19]) for (const z of [-.19, .19]) mesh(new THREE.CylinderGeometry(.035, .045, .32, 6), silver, setting, x, .23, z);
    const diamond = mesh(new THREE.OctahedronGeometry(.42), new THREE.MeshStandardMaterial({ color: '#d7f7fa', metalness: .15, roughness: .08, emissive: '#83cbdc', emissiveIntensity: .25 }), setting, 0, .43, 0); diamond.name = 'ring-diamond'; diamond.scale.y = .75;
  }
  get moving(): boolean { return Math.abs(this.berry.position.z - this.targetZ) > .03; }
  get solved(): boolean { return this.steps === 1 && !this.moving; }
  canPush(x: number, z: number): boolean { return this.steps === 0 && !this.moving && Math.hypot(x - 269.6, z - 70) < 1.8; }
  push(x: number, z: number): boolean { if (!this.canPush(x, z)) return false; this.steps = 1; this.targetZ = 80.5; return true; }
  animate(time: number): void {
    const dt = Math.min(.1, Math.max(0, time - this.lastTime)); this.lastTime = time;
    const delta = this.targetZ - this.berry.position.z;
    const step = Math.min(delta, dt * 5); this.berry.position.z += step; this.berry.rotation.x += step / 1.65;
  }
  blocked(x: number, z: number): boolean {
    if (!this.solved && Math.abs(x - 272) < .9 && z > 65 && z < 75) return true;
    if (Math.hypot(x - 251, z - 66) < 6) return true;
    if (Math.hypot(x - 272, z - this.berry.position.z) < 2) return true;
    if (x > 270.5 && x < 283.8 && (Math.abs(z - 62) < .9 || Math.abs(z - 78) < .9)) return true;
    if (Math.abs(x - 283) < .9 && z > 61 && z < 79) return true;
    return Math.abs(x - 272) < .9 && (Math.abs(z - 64) < 2 || Math.abs(z - 76) < 2);
  }
}
